import { NetworkNodeDto, NetworkTreeDto } from './IAffectedCustomers'
import { AlertSendStatus, NotifiedCustomers } from './IAlert'
import { EventStatus, EventType, IncidentCategory, IncidentCriticality, PlannedWorkCategory, PlannedWorkCriticality } from './IEventEnums'
import { ExternalPlatformMessage } from './IExternalCommunication'

// eslint-disable-next-line sonarjs/redundant-type-aliases
export type EventId = string

export interface NameIdPairDto {
  id: string
  name: string
}

export interface EventSnippet {
  id: EventId
  title: string
  type: EventType
  category: IncidentCategory | PlannedWorkCategory
  criticality: IncidentCriticality | PlannedWorkCriticality
  status: EventStatus
  responsible: NameIdPairDto
  isAllCompanyCustomersAffected: boolean
  fromDate: Date
  toDate: Date
  nextFollowup: Date
  createdOn: Date
  closedDate: Date
  externalReference: string
  affectedEntitiesCount: number
}

export interface EventDto {
  id: EventId
  eventDetails: EventDetailsDto
  type: EventType
  eventPosts: EventPostDto[]
  contacts: Contact[]
  createdOn: Date
  createdBy: NameIdPairDto
  closedDate: Date | null
  lastUpdatedOn: Date
  lastUpdatedBy: NameIdPairDto
  eventChanges: EventChangeDto[]
  networkTree: NetworkTreeDto[]
  affectedNetworkNodes: NetworkNodeDto[]
  affectedPhoneNumbers: string[]
  affectedEmailAddresses: string[]
  isAllCompanyCustomersAffected: boolean
  affectedCustomers: CustomerDto[]
  estimatedAffectedCustomersCount: number
  alerts: AlertSnippetDto[]
  affectedEntitiesCount: number
  affectsBedrift: boolean
  externalPlatformMessages: ExternalPlatformMessage[]
  additionalRelatedAssignments: string[]
}

export interface EventDetailsDto {
  status: EventStatus | string
  title: string
  description: string
  responsible: NameIdPairDto
  fromDate: Date
  toDate: Date | null
  nextFollowup: Date | null
  criticality: IncidentCriticality | PlannedWorkCriticality | string
  category: IncidentCategory | PlannedWorkCategory | string
  salesforceCaseId: string
  fieldServiceCaseId: string
  estimatedDowntime: string
  externalReference: string
  deviation?: EventDeviationDto
  checklist?: EventChecklist
  eTag: string
}

export interface EventDeviationDto {
  text: string
  isDeviation: boolean
  checkedOut?: boolean
  checkOutNote?: string
  checkedOutBy?: NameIdPairDto
  checkedOutAt?: string
}

export interface EventPostDto {
  postId: string
  postedOn: Date
  postedBy: NameIdPairDto
  content: string
}

export interface LocationDto {
  addressId: string
  servicePointId: string
  locationId: string
  connectionNumbers: string[]
  gisApiLocation: GisLocation
}

export interface GisLocation {
  x: number
  y: number
  srid: number
}

export interface CustomerDto {
  customerNumber: string
  customerName: string
  customerType: CustomerType
  customerSalesforceId: string
  affectedAddresses: LocationDto[]
}

export interface EventChangeDto {
  changedOn: Date
  changedBy: NameIdPairDto
  fieldKey?: string
  field: string
  previousValue: string
  newValue: string
  previousValueKey?: string
  newValueKey?: string
}

export interface AlertSnippetDto {
  id: string
  category: string
  type: string
  isSent: boolean
  description: string
  sendOn: Date
  status: AlertSendStatus
  notifiedCustomers?: NotifiedCustomers
  phoneNumbers?: string[]
  emailAddresses?: string[]
}

export enum CustomerType {
  Undefined = 'Undefined',
  Private = 'Private',
  Company = 'Company',
  CompanyWithSla = 'CompanyWithSla',
}

export interface Contact {
  id: string
  name: string
  phone: string
  email: string
  role: ContactRole
}

export enum ContactRole {
  Kontaktperson = 'Kontaktperson',
  Bestiller = 'Bestiller',
}

export interface EventChecklist {
  approvedTime: boolean
  customerListCompleted: boolean
  workCompleted: boolean
  verification: string
  verificationLink: string
  rollbackPlan: string
  rollbackPlanLink: string
}

export interface DocumentUploadModel {
  eventId: string
  file: File | null
  comment: string
}

export interface DocumentDto {
  documentId: string
  eventId: string
  fileName: string
  comment: string | null
  editedBy: string
  date: Date
  contentType: string
}
